/**
 * Section témoignages — avis Google RÉELS de KS Rent Services.
 *
 * Composant SERVEUR (le texte des avis est dans le HTML SSR → preuve sociale +
 * E-E-A-T + lisible par les moteurs IA / AEO). Réutilisable sur tous les silos
 * (chauffeur, location, home) via la prop `locale`.
 *
 * Source : app/data/googleReviews.ts (verbatim, non éditable). On NE génère PAS
 * de schema Review par avis (note de l'entité sur son propre site = self-serving,
 * ignorée par Google) — la note globale reste sur le LocalBusiness (reputation.ts).
 */
import type { Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import { AGGREGATE_RATING } from "@/config/reputation";
import { FEATURED_REVIEWS, GMB } from "@/data/googleReviews";

type ReviewsDict = {
  eyebrow: string;
  heading: string;
  subheading: (rating: string, count: number) => string;
  verified: string;
  seeAll: string;
};

const DICT: I18n<ReviewsDict> = {
  fr: {
    eyebrow: "Avis clients",
    heading: "Ils nous ont fait confiance",
    subheading: (rating, count) =>
      `Note ${rating}/5 sur ${count} avis Google vérifiés — VTC, location et transferts sur la Côte d'Azur.`,
    verified: "Avis Google vérifié",
    seeAll: "Voir tous les avis Google",
  },
  en: {
    eyebrow: "Client reviews",
    heading: "Trusted by our clients",
    subheading: (rating, count) =>
      `Rated ${rating}/5 across ${count} verified Google reviews — chauffeur, rental and transfers on the French Riviera.`,
    verified: "Verified Google review",
    seeAll: "See all Google reviews",
  },
  ar: {
    eyebrow: "آراء العملاء",
    heading: "وثقوا بنا",
    subheading: (rating, count) =>
      `تقييم ${rating}/5 من ${count} مراجعة موثّقة على Google — سائق وتأجير وتنقّلات في الريفييرا الفرنسية.`,
    verified: "مراجعة موثّقة على Google",
    seeAll: "عرض كل مراجعات Google",
  },
};

type ReviewsSectionProps = {
  locale?: Locale;
  /**
   * `full` (défaut) : les verbatims. `badge` : la note, le nombre d'avis et le
   * lien vers la fiche, sans verbatim.
   *
   * POURQUOI `badge` EXISTE. 🟢 MESURE 2026-10-01 sur le HTML construit : les
   * verbatims font 894 mots IDENTIQUES sur les 83 pages ville du silo
   * chauffeur, soit 39 % de /chauffeur/cannes, dont l'unicité réelle tombait
   * à 13 % (suites de 5 mots qu'aucune autre page ne porte). Même décision
   * que Falck Rénovation le 21/09 : sur une page ville, les avis recopiés
   * partout sont du gabarit ; la note reste en badge, les verbatims restent
   * sur l'accueil et le hub, où ils sont uniques.
   */
  variant?: "full" | "badge";
};

export function ReviewsSection({
  locale = "fr",
  variant = "full",
}: ReviewsSectionProps): JSX.Element | null {
  if (!AGGREGATE_RATING || FEATURED_REVIEWS.length === 0) return null;
  const t = tr(DICT, locale);
  // EN garde le point décimal ; FR/AR utilisent la virgule.
  const rating =
    locale === "en"
      ? AGGREGATE_RATING.ratingValue.toFixed(1)
      : AGGREGATE_RATING.ratingValue.toFixed(1).replace(".", ",");

  if (variant === "badge") {
    return (
      <section className="bg-neutral-50 py-8 text-black" aria-label={t.heading}>
        <div className="mx-auto flex max-w-[90rem] flex-col gap-3 px-6 md:flex-row md:items-center md:justify-between md:px-8">
          <p className="text-sm text-black/75">
            <span
              className="me-2 tracking-tight text-gold-700"
              aria-hidden="true"
            >
              ★★★★★
            </span>
            {t.subheading(rating, AGGREGATE_RATING.reviewCount)}
          </p>
          <a
            href={GMB.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex shrink-0 items-center gap-2 self-start rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-medium text-black/80 transition hover:border-primary hover:text-black md:self-auto"
          >
            {t.seeAll} →
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-neutral-50 py-14 text-black" aria-label={t.heading}>
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <p className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55">
          {t.eyebrow}
        </p>
        <h2 className="mb-2 font-space-age text-xl tracking-[0.2em]">
          {t.heading}
        </h2>
        <p className="mb-6 max-w-3xl text-sm text-black/70">
          {t.subheading(rating, AGGREGATE_RATING.reviewCount)}
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURED_REVIEWS.map((review) => {
            const [year, month] = review.date.split("-");
            return (
              <figure
                key={review.id}
                className="flex flex-col rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <figcaption className="text-sm font-semibold text-black">
                    {review.author}
                  </figcaption>
                  <span
                    className="shrink-0 text-sm tracking-tight text-gold-700"
                    aria-label={`${review.rating}/5`}
                  >
                    ★★★★★
                  </span>
                </div>
                <blockquote className="mt-2 whitespace-pre-line text-sm leading-relaxed text-black/75">
                  {review.text}
                </blockquote>
                <p className="mt-3 text-xs text-black/45">
                  {t.verified} · {month}/{year}
                </p>
              </figure>
            );
          })}
        </div>

        <div className="mt-6">
          <a
            href={GMB.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-medium text-black/80 transition hover:border-primary hover:text-black"
          >
            {t.seeAll} →
          </a>
        </div>
      </div>
    </section>
  );
}

export default ReviewsSection;
